import { AuthenticationProvider } from '@sonnen/web-utils/components';
import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AppContextProvider } from './AppContext/AppContext';
import { SentryErrorBoundary } from './components/SentryErrorBoundary/SentryErrorBoundary';
import { useTranslation } from './i18n/i18n';
import { initSentry } from './tools/sentry/sentry';
import { getDefaultLocale, getRedirectUri } from './utils/utils';

// Initialize Sentry before React renders (sonnen-app pattern from app.tsx)
initSentry();

// Dynamic import (lazy route) failures are Promise rejections — they bypass the React
// error boundary entirely and surface as `onunhandledrejection` events.
// Safari / iOS WKWebView reports these as `TypeError: Load failed`.
// Reload once to pick up the fresh bundle after a deployment; the session-storage
// flag prevents infinite reload loops if the new chunk also fails.
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message === 'Load failed' ||
      error.name === 'ChunkLoadError')
  ) {
    const reloadKey = 'chunk_reload_attempted';
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.reload();
    }
  }
});

const element = document.getElementById('root');
const root = ReactDOM.createRoot(element!);

const Main = () => {
  const apiUrl = import.meta.env.VITE_AUTH_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;

  const { changeLanguage } = useTranslation();

  if (!apiUrl || !clientId) {
    throw new Error(
      `Missing required environment variables VITE_AUTH_DOMAIN and VITE_AUTH_CLIENT_ID: ${apiUrl}, ${clientId}`,
    );
  }

  const redirectUri = useMemo(() => getRedirectUri(), []);

  const authConfig = {
    autoLogin: false,
    apiUrl,
    clientId,
    redirectUri,
    scope: 'openid',
  };

  useEffect(() => {
    if (localStorage.getItem('tariff:cookieConsent')) {
      /* Before integrating usercentrics as cookie consent manager,
       * we used a custom-made cookie consent solution
       * that stored the consent in localStorage under the key 'tariff:cookieConsent'.
       * This key is no longer used, so if the key exists, we remove it
       * from user's browser to avoid confusion.
       */
      localStorage.removeItem('tariff:cookieConsent');
    }

    changeLanguage(getDefaultLocale());
  }, []);

  return (
    <React.StrictMode>
      <SentryErrorBoundary boundaryName="app-root">
        <AuthenticationProvider config={authConfig}>
          <AppContextProvider>
            <BrowserRouter basename={process.env.VITE_URL_BASE_PATH || '/'}>
              <App />
            </BrowserRouter>
          </AppContextProvider>
        </AuthenticationProvider>
      </SentryErrorBoundary>
    </React.StrictMode>
  );
};

root.render(<Main />);
